import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProtectedRoute = ({ children, allowedRoles, primaryOwnerOnly, permissionKey }) => {
  const { user, organization, loading } = useAuth();
  const [permAllowed, setPermAllowed] = useState(true);
  const [permLoading, setPermLoading] = useState(!!permissionKey);

  useEffect(() => {
    if (loading) return; // wait for auth to resolve before checking permission
    if (!permissionKey || user?.role?.toLowerCase() === 'owner') {
      setPermLoading(false);
      return;
    }
    let active = true;
    axios.get(`${API}/owner/permissions`)
      .then(res => {
        if (!active) return;
        const role = user?.role?.toLowerCase();
        setPermAllowed(res.data?.permissions?.[permissionKey]?.[role] === true);
      })
      .catch(() => { if (active) setPermAllowed(false); })
      .finally(() => { if (active) setPermLoading(false); });
    return () => { active = false; };
  }, [permissionKey, user?.role, loading]);

  if (loading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Platform-owner-only routes: canonical flag is organization.isOwnerAccount
  if (primaryOwnerOnly && organization?.isOwnerAccount !== true) {
    return <Navigate to="/" replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    // Case-insensitive role check to handle both 'co-owner' and 'Co-Owner'
    const userRoleLower = user.role?.toLowerCase();
    const hasAccess = allowedRoles.some(role => role.toLowerCase() === userRoleLower);
    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }

  // Permission-gated route: non-owner roles must have the feature checkbox enabled
  if (permissionKey && user.role?.toLowerCase() !== 'owner' && !permAllowed) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
